function normalizeText(value = "") {
  return String(value || "").replace(/[’‘]/g, "'").replace(/\s+/g, " ").trim();
}

function messageRole(message = {}) {
  const role = String(message?.role || message?.sender || "").trim().toLowerCase();
  return role === "assistant" || role === "nia" ? "assistant" : "user";
}

function messageContent(message = {}) {
  return normalizeText(message?.content || message?.text || "");
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function fnv1a(value = "") {
  let hash = 0x811c9dc5;
  const text = String(value || "");
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function userMessageTexts(messages = []) {
  return (Array.isArray(messages) ? messages : [])
    .filter((message) => messageRole(message) === "user")
    .map(messageContent)
    .filter(Boolean);
}

function classifyTopic(text = "") {
  const normalized = normalizeText(text).toLowerCase();
  if (!normalized) return "";
  if (
    /\b(previous conversation|previous session|prior session|earlier discussion|continuity|did not receive|didn'?t receive|not sent|reminder date is wrong|cannot access|can'?t access|take this to your leader)\b/i.test(normalized)
  ) {
    return "operational-support";
  }
  if (/\b(christian|church|jesus|bible|faith|religion|fellowship|spiritual|prayer)\b/i.test(normalized)) {
    return "faith";
  }
  if (/\b(pension|retire(?:ment|d)?|living annuity|annuity|withdrawal rate|inflation|tax|own means|monthly income)\b/i.test(normalized)) {
    return "retirement-planning";
  }
  if (/\b(knee|surgery|medical|doctor|hospital|health|pain|illness|injury)\b/i.test(normalized)) {
    return "health";
  }
  if (/\b(friend|friends|family|partner|relationship|boundary|boundaries|terminal illness|conflict)\b/i.test(normalized)) {
    return "relationships";
  }
  if (/\b(follow[\s-]?up|reminder|schedule|scheduled|reschedule|appointment|next session)\b/i.test(normalized)) {
    return "follow-up";
  }
  if (/\b(work|task|tasks|list|priority|prioritise|prioritize|deadline|meeting|focus|marketing|workload)\b/i.test(normalized)) {
    return "work-planning";
  }
  return "";
}

export function compactTranscriptFingerprint(messages = []) {
  const normalized = (Array.isArray(messages) ? messages : [])
    .map((message) => `${messageRole(message)}:${messageContent(message)}`)
    .join("\n");
  return `v1:${(Array.isArray(messages) ? messages.length : 0)}:${fnv1a(normalized)}`;
}

export function latestTranscriptMessageAt(messages = []) {
  return (Array.isArray(messages) ? messages : []).reduce((latest, message) => {
    const timestamp = toMillis(
      message?.createdAt ||
      message?.sentAt ||
      message?.timestamp ||
      message?.updatedAt
    );
    return Math.max(latest, timestamp);
  }, 0);
}

export function inferTranscriptTopic(messages = []) {
  const userTexts = userMessageTexts(messages);
  if (!userTexts.length) return "";
  const latestTopic = classifyTopic(userTexts[userTexts.length - 1]);
  if (latestTopic) return latestTopic;
  return classifyTopic(userTexts.slice(-3).join(" "));
}

export function buildTranscriptSourceVersion(messages = [], followUpFingerprint = "") {
  const entries = Array.isArray(messages) ? messages : [];
  const latestAt = latestTranscriptMessageAt(entries);
  return {
    sourceMessageCount: entries.length,
    sourceLastMessageAt: latestAt ? new Date(latestAt).toISOString() : null,
    sourceTranscriptFingerprint: compactTranscriptFingerprint(entries),
    sourceFollowUpFingerprint: String(followUpFingerprint || ""),
    sourceTopic: inferTranscriptTopic(entries),
  };
}

export function hasPersistedSummarySourceVersion(summary = {}) {
  return Boolean(
    summary &&
    typeof summary === "object" &&
    String(summary.sourceTranscriptFingerprint || "").trim() &&
    Number.isFinite(Number(summary.sourceMessageCount))
  );
}

export function getSummaryFreshness(summary = {}, messages = [], options = {}) {
  if (!summary || typeof summary !== "object") {
    return { current: false, reason: "missing_summary" };
  }
  const status = normalizeText(summary.generationStatus || summary.status || "").toLowerCase();
  if (summary.isCurrent === false || ["stale", "superseded"].includes(status)) {
    return { current: false, reason: status || "marked_non_current" };
  }

  const current = buildTranscriptSourceVersion(messages, options.followUpFingerprint || "");
  if (hasPersistedSummarySourceVersion(summary)) {
    if (Number(summary.sourceMessageCount) !== current.sourceMessageCount) {
      return { current: false, reason: "message_count_changed", currentVersion: current };
    }
    if (
      (
        Object.prototype.hasOwnProperty.call(summary, "sourceLastMessageAt") ||
        Object.prototype.hasOwnProperty.call(summary, "sourceLatestMessageAt")
      ) &&
      toMillis(summary.sourceLastMessageAt || summary.sourceLatestMessageAt) !==
        toMillis(current.sourceLastMessageAt)
    ) {
      return { current: false, reason: "latest_message_timestamp_changed", currentVersion: current };
    }
    if (String(summary.sourceTranscriptFingerprint) !== current.sourceTranscriptFingerprint) {
      return { current: false, reason: "transcript_changed", currentVersion: current };
    }
    if (
      Object.prototype.hasOwnProperty.call(summary, "sourceFollowUpFingerprint") &&
      String(summary.sourceFollowUpFingerprint || "") !== String(current.sourceFollowUpFingerprint || "")
    ) {
      return { current: false, reason: "follow_up_changed", currentVersion: current };
    }
    return { current: true, reason: "version_match", currentVersion: current };
  }

  const generatedAt = toMillis(summary.generatedAt || summary.createdAt);
  const latestAt = latestTranscriptMessageAt(messages);
  if (generatedAt && latestAt && generatedAt < latestAt) {
    return { current: false, reason: "legacy_summary_predates_latest_message", currentVersion: current };
  }
  if ((Array.isArray(messages) ? messages.length : 0) > 0 && !generatedAt) {
    return { current: false, reason: "legacy_summary_source_unknown", currentVersion: current };
  }
  return { current: true, reason: "legacy_version_unknown", currentVersion: current };
}

export function stampSummarySourceVersion(summary = {}, messages = [], options = {}) {
  if (!summary || typeof summary !== "object") return summary;
  const preserveExisting = options.preserveExisting !== false;
  const freshness = getSummaryFreshness(summary, messages, options);
  if (preserveExisting && hasPersistedSummarySourceVersion(summary)) {
    return freshness.current
      ? summary
      : {
          ...summary,
          generationStatus: "stale",
          isCurrent: false,
          staleReason: freshness.reason,
          staleDetectedAt: summary.staleDetectedAt || new Date().toISOString(),
        };
  }
  if (preserveExisting && freshness.reason === "legacy_summary_predates_latest_message") {
    return {
      ...summary,
      generationStatus: "stale",
      isCurrent: false,
      staleReason: freshness.reason,
      staleDetectedAt: summary.staleDetectedAt || new Date().toISOString(),
    };
  }
  if (preserveExisting && freshness.reason === "legacy_summary_source_unknown") {
    return {
      ...summary,
      generationStatus: "stale",
      isCurrent: false,
      staleReason: freshness.reason,
      staleDetectedAt: summary.staleDetectedAt || new Date().toISOString(),
    };
  }

  const version = buildTranscriptSourceVersion(messages, options.followUpFingerprint || "");
  return {
    ...summary,
    ...version,
    sourceTopic:
      normalizeText(summary.sourceTopic) ||
      normalizeText(version.sourceTopic) ||
      "coaching/current-session",
    // Browser-generated summaries are always provisional. Only the trusted
    // Firebase finaliser may bind and promote a summary to canonical `final`.
    generationStatus:
      normalizeText(summary.generationStatus || summary.status) || "draft",
    isCurrent: true,
    staleReason: "",
    staleDetectedAt: null,
  };
}

export function hasMaterialTopicChange(sourceTopic = "", messages = []) {
  const previous = normalizeText(sourceTopic).toLowerCase();
  const current = inferTranscriptTopic(messages);
  return Boolean(previous && current && previous !== current);
}
